import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subMonths } from 'date-fns';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import KPICards from './KPICards';
import ComparisonBanner from './ComparisonBanner';
import CategoryProgressBars from './CategoryProgressBars';
import ChartsPanel from './ChartsPanel';
import PlannedCallout from './PlannedCallout';
import RecentTransactions from './RecentTransactions';
import PullToRefresh from '@/components/shared/PullToRefresh';
import { useExpenses } from '@/hooks/useExpenses';
import { useBudget } from '@/hooks/useBudget';
import { usePlannedForMonth } from '@/hooks/usePlannedExpenses';
import { useAppContext } from '@/context/AppContext';
import { useAuth } from '@/hooks/useAuth';
import { formatMonthYear, currentMonth } from '@/utils/formatters';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user }  = useAuth();
  const { selectedMonth, setSelectedMonth, categories, paymentModes, todoNotifCount } = useAppContext();

  // Current month data
  const { expenses, loading, total: monthTotal, refetch: refetchExpenses } = useExpenses({ month: selectedMonth });
  const { budget, categoryStatus, totalSpent, refetch: refetchBudget }   = useBudget(selectedMonth);
  const { planned, monthTotal: plannedTotal }     = usePlannedForMonth(selectedMonth);

  // Previous month for comparison
  const prevMonth = format(subMonths(new Date(`${selectedMonth}-01`), 1), 'yyyy-MM');
  const { total: prevTotal } = useExpenses({ month: prevMonth });

  // Category totals for charts
  const categoryTotals = React.useMemo(() => {
    const t = {};
    for (const e of expenses) {
      t[e.category] = (t[e.category] ?? 0) + e.amount;
    }
    return t;
  }, [expenses]);

  // Expected cashback: Rupay ₹1/₹100 spent, HDFC ₹3/₹150 spent (per transaction, floor)
  const cashback = React.useMemo(() => {
    return expenses.reduce((total, e) => {
      if (e.paymentMode === 'rupay' && e.amount >= 100) {
        return total + Math.floor(e.amount / 100);
      }
      if (e.paymentMode === 'hdfc' && e.amount >= 150) {
        return total + Math.floor(e.amount / 150) * 3;
      }
      return total;
    }, 0);
  }, [expenses]);

  const greeting = user?.displayName?.split(' ')[0] ?? 'there';

  return (
    <div className="flex flex-col h-full">
      <Header
        title="₹ NammaExpenseTracker"
        subtitle={
          todoNotifCount > 0
            ? `Hi, ${greeting}! 👋 · 🔔 ${todoNotifCount} notification${todoNotifCount !== 1 ? 's' : ''} pending`
            : `${formatMonthYear(new Date(`${selectedMonth}-01`))} · Hi, ${greeting}! 👋`
        }
      />

      {/* Month picker */}
      <div className="px-3 pt-2 flex justify-end">
        <input
          type="month"
          value={selectedMonth}
          max={currentMonth()}
          onChange={e => setSelectedMonth(e.target.value)}
          className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
          aria-label="Select month"
        />
      </div>

      <PullToRefresh
        onRefresh={async () => { await Promise.all([refetchExpenses(), refetchBudget()]); }}
        className="flex-1 overflow-y-auto scrollbar-none pb-20 fade-in"
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <KPICards
              budget={budget?.totalBudget ?? 0}
              spent={totalSpent}
              remaining={(budget?.totalBudget ?? 0) - totalSpent}
              prevMonthTotal={prevTotal}
              cashback={cashback}
            />

            <ComparisonBanner
              currentTotal={monthTotal}
              prevTotal={prevTotal}
              currentMonthLabel={formatMonthYear(new Date(`${selectedMonth}-01`))}
              prevMonthLabel={formatMonthYear(new Date(`${prevMonth}-01`))}
            />

            <h2 className="text-xs font-bold uppercase tracking-wide text-gray-600 px-3 mb-2">
              Category Budgets
            </h2>
            <CategoryProgressBars categoryStatus={categoryStatus} categories={categories} />

            <ChartsPanel categoryTotals={categoryTotals} expenses={expenses} categories={categories} paymentModes={paymentModes} />

            <PlannedCallout planned={planned} monthTotal={plannedTotal} />

            <RecentTransactions expenses={expenses} />
          </>
        )}
      </PullToRefresh>

      {/* FAB */}
      <button
        className="fab"
        onClick={() => navigate('/expenses/add')}
        aria-label="Add new expense"
      >
        ＋
      </button>

      <BottomNav />
    </div>
  );
}

